import React, { useRef, useEffect, Suspense, lazy } from "react";
import { ThemeProvider, createTheme, responsiveFontSizes } from "@mui/material/styles";
import CssBaseline from "@mui/material/CssBaseline";
import "./index.css";
import AOS from "aos";
import { COLOR_PRIMARY } from "./constants";
import NavigationBar from "./components/first-section/NavigationBar";
import FirstSection from "./components/first-section/FirstSection";
import LoadingIndicator from "./components/common/LoadingIndicator";
import ScrollToTop from "./components/common/ScrollToTop";

// Lazy load components
const SecondSection = lazy(() => import("./components/second-section/SecondSection"));
const ServiceSection = lazy(() => import("./components/third-section/ServiceSection"));
const FourthSection = lazy(() => import("./components/fourth-section/FourthSection"));
const Footer = lazy(() => import("./components/footer/Footer"));

// Create a responsive theme
let theme = createTheme({
  palette: {
    primary: {
      main: COLOR_PRIMARY,
    },
    background: {
      default: "#ffffff",
    },
  },
  typography: {
    fontFamily: '"Rubik", sans-serif',
    fontSize: 16,
    h4: {
      fontWeight: 600,
      fontSize: '2.2rem',
    },
    h5: {
      fontWeight: 500,
      fontSize: '1.8rem',
    },
    h6: {
      fontWeight: 500,
      fontSize: '1.4rem',
    },
    body1: {
      fontSize: '1.1rem',
    },
    body2: {
      fontSize: '1rem',
    },
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: 10,
          textTransform: "none",
          fontSize: '1.1rem',
          padding: '10px 24px',
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 16,
        },
      },
    },
  },
});

// Make fonts responsive
theme = responsiveFontSizes(theme);

function App() {
  const secondSectionRef = useRef<HTMLDivElement>(null);
  const serviceSectionRef = useRef<HTMLDivElement>(null);
  const fourthSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    AOS.init({ 
      duration: 1000,
      once: true,
      easing: 'ease-out-cubic',
    });
  }, []);

  const scrollToSection = (ref: React.RefObject<HTMLDivElement>) => {
    ref.current?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <div>
        <NavigationBar
          scrollToSection={scrollToSection}
          refs={{
            second: secondSectionRef,
            service: serviceSectionRef,
            fourth: fourthSectionRef,
          }}
        />
        <FirstSection />
        <Suspense fallback={<LoadingIndicator />}>
          <div ref={secondSectionRef} data-aos="fade-up">
            <SecondSection />
          </div>
          <div ref={serviceSectionRef} data-aos="fade-up">
            <ServiceSection />
          </div>
          <div ref={fourthSectionRef} data-aos="fade-up">
            <FourthSection />
          </div>
          <Footer />
        </Suspense>
        <ScrollToTop />
      </div>
    </ThemeProvider>
  );
}

export default App;
